import { useEffect, useState } from "react";
import { isNativeApp } from "@/models/platform";
import { useUserSlice } from "@/models/useUserSlice";
import { i18n, appLanguages } from "@/locales";
import { logFunnelEvent } from "@/fetchData/datadog";
import { CONTENT_LANGUAGES, type ContentLanguage } from "@/models/types";
import NativeApp from "@/pages/native";
import WebApp from "@/pages/web";

function App() {
  const userData = useUserSlice((state) => state.userData);
  const [localeReady, setLocaleReady] = useState(false);

  // Apply user's saved language preference on cold opens (no URL params)
  useEffect(() => {
    if (!userData?.deviceLanguage) return;
    const supported = appLanguages.map((l) => l?.value);
    const userLang = userData.deviceLanguage.slice(0, 2);
    if (userLang && supported.includes(userLang) && userLang !== i18n.language) {
      i18n.changeLanguage(userLang);
    }
  }, [userData?.deviceLanguage]);

  // Set locale: URL params > device language > browser language
  useEffect(() => {
    if (localeReady) return;

    const supported = appLanguages.map((l) => l?.value);
    const urlParams = new URLSearchParams(window.location.search);

    // URL appLang param (ad campaigns) — highest priority for UI language
    const urlAppLang = urlParams.get("appLang")?.slice(0, 2);
    if (urlAppLang && supported.includes(urlAppLang) && urlAppLang !== i18n.language) {
      i18n.changeLanguage(urlAppLang);
    }

    // URL contentLang param — set content language if valid
    const urlContentLang = urlParams.get("contentLang");
    if (urlContentLang && CONTENT_LANGUAGES.some((l) => l.code === urlContentLang)) {
      useUserSlice.getState().setContentLanguage(urlContentLang as ContentLanguage);
    }

    // If URL params were provided, skip device detection
    if (urlAppLang || urlContentLang) {
      setLocaleReady(true);
      return;
    }

    // On native, detect device language as fallback
    if (isNativeApp()) {
      import("@capacitor/device")
        .then(({ Device }) => Device.getLanguageTag())
        .then(({ value }) => {
          const deviceLang = value.slice(0, 2);
          if (supported.includes(deviceLang) && deviceLang !== i18n.language) {
            i18n.changeLanguage(deviceLang);
          }
        })
        .catch(() => {})
        .finally(() => setLocaleReady(true));
      return;
    }

    // On web, navigator.language was already used in sync init
    setLocaleReady(true);
  }, [localeReady]);

  if (!localeReady) return null;

  logFunnelEvent("[1] app:loaded", {
    referrer: typeof document !== "undefined" ? document.referrer : "",
  });

  if (isNativeApp()) return <NativeApp />;

  return <WebApp />;
}

export default App;
